/**
 * Carries an ad's UTM tags from the landing URL through to app sign-up.
 *
 * The app already records the query string it is opened with against the new account (the
 * `utm` column on Users), so attributing a signup to an advert only needs the tags to survive
 * the hop from www to app.careerskitchen.com. Every sign-up link on this site -- header, CTAs,
 * Prismic-authored buttons -- points at `${GATSBY_APP_URL}/sign-up` with no query string, so
 * until now the tags were dropped there.
 *
 * Held in memory only. Gatsby navigates client-side, so a module variable survives a visitor
 * reading two or three pages before signing up, and nothing is written to the browser, which
 * keeps this outside the cookie rules entirely. A full reload or a new tab loses it; that is
 * the accepted cost of not storing anything.
 *
 * Only the five standard UTM keys are forwarded. Click IDs such as `fbclid` are not: the app has
 * no use for them, and they are long enough to overflow the column.
 */
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Ours are short (`signup-test-nov26`); anything longer is not one of ours.
const MAX_VALUE_LENGTH = 40;

let captured = null;

const isBrowser = () => typeof window !== 'undefined';

export const getAttribution = () => (captured ? { ...captured } : null);

// Test seam.
export const resetAttribution = () => {
  captured = null;
};

/**
 * Records UTM tags from a query string. The most recent tagged arrival wins; an untagged page
 * view leaves an earlier capture alone, because internal links carry no tags.
 */
export const captureAttribution = (search) => {
  if (!search) return;

  let params;
  try {
    params = new URLSearchParams(search);
  } catch (error) {
    return;
  }

  const picked = UTM_KEYS.reduce((acc, key) => {
    const value = (params.get(key) || '').trim().slice(0, MAX_VALUE_LENGTH);
    return value ? { ...acc, [key]: value } : acc;
  }, {});

  if (Object.keys(picked).length) captured = picked;
};

const appOrigin = () => {
  try {
    return new URL(process.env.GATSBY_APP_URL).origin;
  } catch (error) {
    return null;
  }
};

export const isSignUpUrl = (href) => {
  if (!href || !isBrowser()) return false;

  const origin = appOrigin();
  if (!origin) return false;

  try {
    const url = new URL(href, window.location.href);
    return url.origin === origin && url.pathname.replace(/\/+$/, '') === '/sign-up';
  } catch (error) {
    return false;
  }
};

/**
 * Returns the href with captured tags appended. Tags already on the link win, so a Prismic
 * author who hard-codes a campaign on a specific button is not overridden.
 */
export const withAttribution = (href) => {
  if (!captured || !isSignUpUrl(href)) return href;

  const url = new URL(href, window.location.href);

  Object.entries(captured).forEach(([key, value]) => {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  });

  return url.toString();
};

/**
 * Decorates sign-up links at the moment they are used, rather than at render, so it covers
 * every link on the site including Prismic-authored ones without touching each component.
 * mousedown/touchstart/keydown run before the browser reads the href for a click, middle-click
 * or "open in new tab"; click is the fallback. Same pattern as Google's cross-domain linker.
 *
 * `onSignUpClick` fires once per activation, for the click event only.
 */
export const installSignUpLinkDecorator = ({ onSignUpClick } = {}) => {
  if (!isBrowser()) return () => {};

  const handler = (event) => {
    const anchor = event.target && event.target.closest && event.target.closest('a[href]');
    if (!anchor || !isSignUpUrl(anchor.href)) return;

    const decorated = withAttribution(anchor.href);
    if (decorated !== anchor.href) anchor.href = decorated;

    if (event.type === 'click' && onSignUpClick) onSignUpClick(anchor);
  };

  const events = ['mousedown', 'touchstart', 'keydown', 'click'];
  events.forEach((type) =>
    document.addEventListener(type, handler, { capture: true, passive: true })
  );

  return () =>
    events.forEach((type) => document.removeEventListener(type, handler, { capture: true }));
};
